import React, { createContext, useContext, useState, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { api } from '../utils/api';

const DashboardContext = createContext();

export function useDashboard() {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within a DashboardProvider');
  }
  return context;
}

export function DashboardProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch dashboard data
  const fetchDashboard = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const response = await api.get('/api/progress/dashboard');
      setDashboardData(response);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Refresh dashboard (force reload)
  const refreshDashboard = useCallback(async () => {
    await fetchDashboard();
  }, [fetchDashboard]);

  // Save speed training session
  const saveSpeedSession = useCallback(async (sessionData) => {
    try {
      const response = await api.post('/api/progress/speed', sessionData);
      // Refresh dashboard to show updated stats
      await fetchDashboard();
      return { success: true, data: response };
    } catch (err) {
      console.error('Failed to save speed session:', err);
      return { success: false, error: err.message || 'Failed to save session' };
    }
  }, [fetchDashboard]);

  const value = {
    dashboardData,
    loading,
    error,
    fetchDashboard,
    refreshDashboard,
    saveSpeedSession
  };

  return (
    <DashboardContext.Provider value={value}>
      {children}
    </DashboardContext.Provider>
  );
}

